import { T, Bone } from '../../reportTokens';
import type { OrderLogFinancials } from '../OrderLog';
import { getCurrencySymbol } from '../../../../utils/currency';
import type { OrderDetailResponse, OrderOfferItem, OrderLoyaltyInfo, RemovedItemModifier } from '../../../../interface/clickHouseInterface';
import { usePullToRefresh } from '../../../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../../../utils/pullToRefresh/PullToRefreshIndicator';
import { useServiceChargeEnabled } from '../../../../hooks/useServiceChargeEnabled';
import {
    emptyComponents,
    deriveRefundType,
    refundStatusLabel,
    type RefundComponentKey,
    type RefundRecord,
    type RefundDetail,
    type RefundTenderReturn,
    type RefundType,
} from '../../refunds/refundModel';
import { reasonGroupOf, NO_REASON } from '../../refunds/refundReasons';
import { RefundHistoryBlock } from '../../refunds/RefundHistoryBlock';
import './style.scss';

// ─── helpers ─────────────────────────────────────────────────────────────────

const num = (v: any): number => Number(v ?? 0) || 0;

// A negative value (e.g. Delivery Fee arriving as -9.75) must read "-$9.75", not "$-9.75" —
// `toLocaleString` puts the sign right against the number, so the symbol has to go after it.
const fmtCurrency = (v: any) => {
    const n = num(v);
    const formatted = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${n < 0 ? '-' : ''}${getCurrencySymbol()}${formatted}`;
};

const fmtPhone = (raw: any): string => {
    if (!raw) return '—';
    const digits = String(raw).replace(/\D/g, '');
    if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    return String(raw);
};

const fmtOrderTime = (raw: any): string => {
    if (!raw) return '—';
    try {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return String(raw);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
        return String(raw);
    }
};

const statusColor = (s: string): string => {
    if (/refund|cancel/i.test(s)) return T.red;
    if (/partial/i.test(s)) return T.orange;
    return T.green;
};

// ─── types ───────────────────────────────────────────────────────────────────

interface NormalisedItem {
    name: string;
    /** {name, price} per modifier — same shape a removed item's modifiers carry. */
    modifiers: RemovedItemModifier[] | null;
    qty: number;
    unitPrice: number;
    lineTotal: number;
    /** The item's own per-unit price, excluding modifiers — shown in the Items table's Price column. */
    itemUnitPrice: number;
    offerName: string | null;
}

interface NormalisedDetail {
    orderType: string;
    orderTime: string;
    orderStatus: string;
    customerName: string | null;
    phone: string;
    email: string | null;
    guests: number | null;
    tableName: string | null | undefined;
    deliveryAddress: string | null;
    isOffPremise: boolean;
    staffName: string | null;
    items: NormalisedItem[];
    removedItems: OrderDetailResponse['removedItems'];
    subtotal: number;
    discount: number;
    offers: OrderOfferItem[];
    loyalty: OrderLoyaltyInfo | null;
    tax: number;
    tip: number;
    gratuityAmt: number;
    serviceCharge: number;
    deliveryCharges: number;
    convenienceFee: number;
    cardProcessingFee: number;
    /** Total amount refunded on this order — shown as its own Summary + Financial Breakdown line. */
    refundAmount: number;
    total: number;
}

/**
 * Modifiers arrive as {name, price, quantity, totalPrice}[] on the typed path. The legacy
 * fallback (never hit today — every call site passes `legacyDetail={undefined}`) may still
 * carry an older shape (a joined string, or a plain string[], or an object missing
 * quantity/totalPrice), so those are coerced into the same shape rather than rendered as-is —
 * quantity defaults to 1 and totalPrice falls back to `price` when the source omits them.
 */
function normaliseModifiers(raw: any): RemovedItemModifier[] | null {
    if (!raw) return null;
    if (Array.isArray(raw)) {
        if (raw.length === 0) return null;
        return raw.map((m: any) => {
            if (typeof m === 'string') return { name: m, price: 0, quantity: 1, totalPrice: 0 };
            const price = Number(m?.price ?? 0) || 0;
            const quantity = Number(m?.quantity ?? 1) || 1;
            const totalPrice = Number(m?.totalPrice ?? price) || 0;
            return { name: String(m?.name ?? ''), price, quantity, totalPrice };
        });
    }
    return String(raw).split(',').map((s) => s.trim()).filter(Boolean).map((name) => ({ name, price: 0, quantity: 1, totalPrice: 0 }));
}

// ─── normalise typed OR legacy payload into one shape ────────────────────────

function normalise(detail: OrderDetailResponse | null, legacyDetail: any): NormalisedDetail | null {
    const isTyped = !!detail;

    if (!isTyped && !legacyDetail) return null;

    const orderType: string = isTyped
        ? detail!.orderType
        : String(legacyDetail?.orderType ?? legacyDetail?.channelName ?? legacyDetail?.channel ?? '');

    const items: NormalisedItem[] = isTyped
        ? detail!.items.map(it => ({
            name: it.itemName,
            modifiers: normaliseModifiers(it.modifiers),
            qty: it.qty,
            unitPrice: it.unitPrice,
            lineTotal: it.lineTotal,
            itemUnitPrice: it.itemUnitPrice,
            offerName: it.offerName || null,
        }))
        : (legacyDetail?.items ?? legacyDetail?.orderItems ?? legacyDetail?.lineItems ?? []).map((it: any) => ({
            name: it.itemName ?? it.name ?? it.menuItemName ?? '—',
            modifiers: normaliseModifiers(it.modifiers ?? it.modifications ?? it.addOns ?? it.specialInstructions ?? null),
            offerName: it.offerName || null,
            qty: Number(it.quantity ?? it.qty ?? it.count ?? 1),
            unitPrice: Number(it.unitPrice ?? it.price ?? it.itemPrice ?? 0),
            // FE calc: unitPrice × qty as last fallback
            lineTotal: Number(it.lineTotal ?? it.totalPrice ?? it.amount ?? (it.unitPrice ?? it.price ?? 0) * (it.quantity ?? it.qty ?? 1)),
            itemUnitPrice: Number(it.itemUnitPrice ?? it.unitPrice ?? it.price ?? it.itemPrice ?? 0),
        }));

    const offers: OrderOfferItem[] = isTyped ? (detail!.offers ?? []) : [];

    const loyalty: OrderLoyaltyInfo | null = isTyped ? (detail!.loyalty ?? null) : null;

    // Summary's Discounts total = every discount on the order (order-level +
    // item-level offers + loyalty redemption). `discounts` alone omits the
    // item-level offers and comes back as 0 on an order that has them, which
    // blanked the row while the offer lines below it still listed their amounts.
    // `totalDiscount` is the same field the Financial Breakdown's Discount line
    // reads (see financialsFromDetail), so the two can never disagree; summing
    // what this panel itself renders is the fallback for a payload without it.
    const offerPlusLoyalty = offers.reduce((sum, o) => sum + (Number(o.offerDiscount) || 0), 0)
        + Number(loyalty?.loyaltyValue ?? 0);
    const discount = isTyped
        ? (detail!.totalDiscount ?? (offerPlusLoyalty || detail!.discounts))
        : Number(legacyDetail?.totalDiscount ?? legacyDetail?.discount ?? legacyDetail?.discountAmount ?? 0);

    const tax = isTyped ? detail!.tax : Number(legacyDetail?.tax ?? legacyDetail?.taxAmount ?? legacyDetail?.totalTax ?? 0);
    const tip = isTyped ? detail!.tip : Number(legacyDetail?.tip ?? legacyDetail?.tipAmount ?? 0);

    // FE calc: sum of lineTotals as subtotal fallback for legacy
    const subtotal = isTyped
        ? detail!.subtotal
        : (items.reduce((s, it) => s + it.lineTotal, 0) || Number(legacyDetail?.subTotal ?? legacyDetail?.subtotal ?? 0));

    // FE calc: subtotal - discount + tax + tip as total fallback for legacy
    const total = isTyped
        ? detail!.total
        : Number(legacyDetail?.amount ?? legacyDetail?.totalAmount ?? legacyDetail?.total ?? legacyDetail?.orderTotal ?? (subtotal - discount + tax + tip));

    const rawOrderTime = isTyped ? detail!.orderTime : (legacyDetail?.orderTime ?? legacyDetail?.completedAt ?? legacyDetail?.createdAt ?? legacyDetail?.time);

    return {
        orderType,
        orderTime: fmtOrderTime(rawOrderTime),
        orderStatus: isTyped ? detail!.orderStatus : (legacyDetail?.orderStatus ?? legacyDetail?.status ?? 'Completed'),
        customerName: isTyped ? (detail!.customerName || null) : (legacyDetail?.customerName ?? legacyDetail?.customer ?? legacyDetail?.guestName ?? null),
        phone: fmtPhone(isTyped ? detail!.customerPhone : (legacyDetail?.customerPhone ?? legacyDetail?.phone ?? legacyDetail?.mobileNumber ?? null)),
        email: isTyped ? (detail!.customerEmail || null) : (legacyDetail?.customerEmail ?? legacyDetail?.email ?? null),
        guests: isTyped ? (detail!.guests ?? null) : (legacyDetail?.guests ?? null),
        tableName: isTyped
            ? (detail!.tableName !== undefined ? (detail!.tableName || null) : undefined)
            : ('tableName' in (legacyDetail ?? {}) ? (legacyDetail.tableName || null) : undefined),
        deliveryAddress: isTyped ? detail!.deliveryAddress : (legacyDetail?.deliveryAddress ?? legacyDetail?.address ?? null),
        isOffPremise: /delivery|doordash|uber|pickup|catering/i.test(orderType),
        staffName: isTyped ? (detail!.staffName || null) : (legacyDetail?.staffName ?? legacyDetail?.serverName ?? legacyDetail?.employeeName ?? legacyDetail?.staff ?? null),
        items,
        removedItems: isTyped ? (detail!.removedItems ?? []) : [],
        subtotal,
        discount,
        offers,
        loyalty,
        tax,
        tip,
        gratuityAmt: isTyped ? (detail!.gratuity ?? 0) : Number(legacyDetail?.gratuity ?? 0),
        serviceCharge: isTyped ? (detail!.serviceCharge ?? 0) : Number(legacyDetail?.serviceCharge ?? 0),
        deliveryCharges: isTyped ? (detail!.deliveryCharges ?? 0) : Number(legacyDetail?.deliveryCharges ?? 0),
        convenienceFee: isTyped ? (detail!.convenienceFee ?? 0) : Number(legacyDetail?.convenienceFee ?? 0),
        cardProcessingFee: isTyped ? (detail!.cardProcessingFee ?? 0) : Number(legacyDetail?.cardProcessingFee ?? 0),
        refundAmount: isTyped ? (detail!.refundAmount ?? 0) : Number(legacyDetail?.refundAmount ?? 0),
        total,
    };
}

// ─── sub-components ──────────────────────────────────────────────────────────

// Item | Qty | Unit Price | Price — modifiers are no longer crammed into their own
// column (that's what previously forced `.join(', ')` on an array of {name, price}
// objects, rendering "[object Object]"). Each modifier is now its own indented row
// directly under its item, sharing the same four columns, with its own qty and prices.
//
// Unit Price is what one of the thing costs; Price is that × its quantity. An item's
// pair is its OWN price, excluding modifiers (`itemUnitPrice`, not `unitPrice`/
// `lineTotal`, both of which fold the modifiers in) — the modifier rows underneath
// carry their own money, so folding it in here too would show it twice.
const ITEM_GRID = '1fr 0.34fr 0.8fr 0.8fr';
const EM_DASH = '—';

function ItemsHeader() {
    return (
        <div className="ods-items-header" style={{ gridTemplateColumns: ITEM_GRID }}>
            <div className="ods-col-head">Item</div>
            <div className="ods-col-head" style={{ textAlign: 'right' }}>Qty</div>
            <div className="ods-col-head" style={{ textAlign: 'right' }}>Unit Price</div>
            <div className="ods-col-head" style={{ textAlign: 'right' }}>Price</div>
        </div>
    );
}

function ModifierRows({ modifiers }: { modifiers: RemovedItemModifier[] | null }) {
    if (!modifiers || modifiers.length === 0) return null;
    return (
        <>
            {modifiers.map((m, i) => (
                <div key={i} className="ods-item-row ods-mod-row" style={{ gridTemplateColumns: ITEM_GRID }}>
                    <div className="ods-mod-name">{m.name}</div>
                    <div className="ods-mod-qty">{m.quantity ?? 1}</div>
                    <div className="ods-mod-unit">{fmtCurrency(m.price)}</div>
                    <div className="ods-mod-price">{fmtCurrency(m.totalPrice ?? m.price)}</div>
                </div>
            ))}
        </>
    );
}

function ItemsTable({ items }: { items: NormalisedItem[] }) {
    return (
        <div className="ods-card">
            <div className="ods-card__label">Items</div>
            <ItemsHeader />
            {items.map((it, idx) => (
                <div
                    key={idx}
                    className="ods-item-group"
                    style={{ borderBottom: idx < items.length - 1 ? `1px solid ${T.divider}` : 'none' }}
                >
                    <div className="ods-item-row" style={{ gridTemplateColumns: ITEM_GRID }}>
                        <div>
                            <div className="ods-item-name">{it.name}</div>
                            {it.offerName && (
                                <div className="ods-cancel-offer" style={{ color: T.t3 }}>
                                    {it.offerName}
                                </div>
                            )}
                        </div>
                        <div className="ods-item-qty">{it.qty}</div>
                        <div className="ods-item-unit">{fmtCurrency(it.itemUnitPrice)}</div>
                        <div className="ods-item-total">{fmtCurrency(it.itemUnitPrice * it.qty)}</div>
                    </div>
                    <ModifierRows modifiers={it.modifiers} />
                </div>
            ))}
        </div>
    );
}

function CancelledItemsTable({ items }: { items: NonNullable<OrderDetailResponse['removedItems']> }) {
    return (
        <div className="ods-card ods-card--cancelled" style={{ border: `1px solid ${T.red}50`, background: `${T.red}08` }}>
            <div className="ods-card__label" style={{ color: T.red }}>Cancelled Items</div>
            <ItemsHeader />
            {items.map((ri, idx) => (
                <div
                    key={idx}
                    className="ods-item-group"
                    style={{ borderBottom: idx < items.length - 1 ? `1px solid ${T.divider}` : 'none' }}
                >
                    <div className="ods-item-row" style={{ gridTemplateColumns: ITEM_GRID }}>
                        <div>
                            <div className="ods-item-name">{ri.item}</div>
                            {ri.offerName && (
                                <div className="ods-cancel-offer" style={{ color: T.t3 }}>
                                    {ri.offerName}
                                </div>
                            )}
                            {ri.cancelReason && (
                                <div className="ods-cancel-reason" style={{ color: T.orange }}>
                                    <span style={{ color: T.t3 }}>Reason: </span>{ri.cancelReason}
                                </div>
                            )}
                        </div>
                        <div className="ods-item-qty">{ri.quantity}</div>
                        <div className="ods-item-unit">{ri.unitPrice == null ? EM_DASH : fmtCurrency(ri.unitPrice)}</div>
                        <div className="ods-item-total" style={{ color: T.red }}>{fmtCurrency(ri.total)}</div>
                    </div>
                    <ModifierRows modifiers={ri.modifiers} />
                </div>
            ))}
        </div>
    );
}

/**
 * Financial waterfall (ORD-1.4) — the same components the Order Log can show as
 * columns, itemised for one check. `null` renders an em dash: unavailable, not
 * zero. Rendered only when the caller has a breakdown to show.
 */
export function FinancialBreakdown({ f, refund, showServiceCharge = false }: { f: OrderLogFinancials; refund?: RefundRecord | null; showServiceCharge?: boolean }) {
    const rows: { label: string; value: number | null; reduction?: boolean; refundKey?: RefundComponentKey }[] = [
        { label: 'Item Total', value: f.itemTotal, refundKey: 'item' },
        { label: 'Discount', value: f.discount, reduction: true },
        // Service Charge is hidden for now — no endpoint returns the value, so the
        // row could only ever render an em dash. `serviceCharge` is still carried on
        // OrderLogFinancials (as null), so restoring this line is the whole change.
        // { label: 'Service Charge', value: f.serviceCharge },
        { label: 'Delivery Fee', value: f.deliveryFee, refundKey: 'deliveryFee' },
        // `otherCharge` carries the API's `convenienceFee` (see financialsFromDetail
        // below and toOrderLogRow) — the row is labelled for what the value actually is.
        { label: 'Convenience Fee', value: f.otherCharge, refundKey: 'convenienceFee' },
        { label: 'Tax', value: f.tax, refundKey: 'tax' },
        { label: 'Tips', value: f.tips, refundKey: 'tip' },
        { label: 'Gratuity', value: f.gratuity, refundKey: 'gratuity' },
        // Service Charge — only for outlets that capture one (`uiFeatureFlags.isServiceChargeEnabled`,
        // same gate as the Order Log column); for those it always renders, 0 included. Its own
        // line, never folded into Gratuity.
        ...(showServiceCharge ? [{ label: 'Service Charge', value: f.serviceCharge, refundKey: 'serviceCharge' as RefundComponentKey }] : []),
        // REF-1 — a card processing fee is never refundable; this row's Refunded
        // cell always reads an em dash, deliberately unconditional on `refund`.
        { label: 'Card Processing Fee', value: f.processingFee },
    ];
    // REF-5 — a Refunded column against each line, present only when the order
    // actually carries a refund event: a column of em dashes on every unrefunded
    // order would be noise.
    const refunded = (key?: RefundComponentKey): number | null => {
        if (!key || !refund) return null;
        const v = refund.components[key];
        return v > 0 ? v : null;
    };
    return (
        <>
            <div className="ods-card__label ods-breakdown-label" style={{ marginBottom: 4 }}>Financial Breakdown</div>
            {refund && (
                <div className="ods-summary-row" style={{ borderBottom: 'none', paddingBottom: 2 }}>
                    <span />
                    <span className="ods-summary-val" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: '#9CA3B8' }}>Charged · Refunded</span>
                </div>
            )}
            {rows.map(r => {
                const ref = refunded(r.refundKey);
                return (
                    <div key={r.label} className="ods-summary-row" style={{ borderBottom: `1px solid ${T.divider}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                        <span className="ods-summary-label">{r.label}</span>
                        <span style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                            <span className="ods-summary-val" style={{ color: r.value === null ? T.inkLight : r.reduction && r.value > 0 ? T.inkMid : T.inkDark, fontVariantNumeric: 'tabular-nums' }}>
                                {r.value === null ? '—' : `${r.reduction && r.value > 0 ? '−' : ''}${fmtCurrency(r.value)}`}
                            </span>
                            {refund && (
                                <span
                                    className="ods-summary-val"
                                    style={{ fontVariantNumeric: 'tabular-nums', minWidth: 62, textAlign: 'right', color: ref ? T.red : '#9CA3B8', fontWeight: ref ? 600 : undefined }}
                                >
                                    {ref ? fmtCurrency(ref) : '—'}
                                </span>
                            )}
                        </span>
                    </div>
                );
            })}
            {f.refundAmount != null && f.refundAmount > 0 && (
                <div className="ods-summary-row" style={{ borderBottom: `1px solid ${T.divider}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                    <span className="ods-summary-label">Refund Amount</span>
                    <span className="ods-summary-val" style={{ color: T.red, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {fmtCurrency(f.refundAmount)}
                    </span>
                </div>
            )}
            <div className="ods-summary-total">
                <span className="ods-total-label">Grand Total</span>
                <span className="ods-total-val" style={{ color: T.inkDark }}>{f.grandTotal === null ? '—' : fmtCurrency(f.grandTotal)}</span>
            </div>
            {/* REF-5 — Refund History sits directly below the Financial Breakdown. */}
            <RefundHistoryBlock refund={refund ?? null} />
        </>
    );
}

/** null = the field is unavailable; 0 = a genuine zero. The two never merge. */
const numOrNull = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
};

/**
 * The order-detail response carries the whole waterfall for this one order, so
 * the slide-over derives it here rather than depending on the caller having a
 * log row. That is what makes the breakdown work on every surface — including
 * the Unpaid drill and the Discounts drill, which open the slider from a row
 * that has no financial columns of its own.
 *
 * Service Charge reads the endpoint's `serviceCharge` — every order reports one,
 * defaulting to 0 when the response omits it.
 */
export function financialsFromDetail(detail: OrderDetailResponse | null, d: NormalisedDetail | null = null, logFinancials: OrderLogFinancials | null = null): OrderLogFinancials | null {
    if (detail) {
        return {
            itemTotal: numOrNull(detail.subtotal),
            // Discount = totalDiscount (order- + item-level offers). The detail response's
            // own value wins; otherwise the order-log row's (which reads totalDiscount);
            // `discounts` is the last resort, as it omits item-level offers.
            discount: numOrNull(detail.totalDiscount ?? logFinancials?.discount ?? detail.discounts),
            serviceCharge: numOrNull(detail.serviceCharge) ?? 0,
            deliveryFee: numOrNull(detail.deliveryCharges),
            otherCharge: numOrNull(detail.convenienceFee),
            tax: numOrNull(detail.tax),
            tips: numOrNull(detail.tip),
            gratuity: numOrNull(detail.gratuity),
            processingFee: numOrNull(detail.cardProcessingFee),
            grandTotal: numOrNull(detail.total),
            refundAmount: numOrNull(detail.refundAmount),
        };
    }
    if (!d) return null;
    // Legacy payload — normalise() already folded the older field names.
    return {
        itemTotal: d.subtotal,
        discount: d.discount,
        serviceCharge: 0,
        deliveryFee: d.deliveryCharges,
        otherCharge: d.convenienceFee,
        tax: d.tax,
        tips: d.tip,
        gratuity: d.gratuityAmt,
        processingFee: d.cardProcessingFee,
        grandTotal: d.total,
        refundAmount: d.refundAmount,
    };
}

/** REF-5 — the component names GET /sales/live/v2/order-detail sends, normalised
 * (lowercased, non-letters stripped) so 'Delivery Fee' and 'deliveryFee' both match. */
const REFUND_COMPONENT_MAP: Record<string, RefundComponentKey> = {
    item: 'item', items: 'item', itemtotal: 'item',
    tax: 'tax',
    tip: 'tip', tips: 'tip',
    gratuity: 'gratuity',
    deliveryfee: 'deliveryFee',
    conveniencefee: 'convenienceFee',
    servicecharge: 'serviceCharge',
    cardprocessingfee: 'cardProcessingFee',
};
const REFUND_TYPE_MAP: Record<string, RefundType> = { full: 'full', item: 'item', fee: 'fee' };

/**
 * REF-5 — one order carries at most one refund event (`detail.refundHistory`), mapped onto
 * the shared `RefundRecord` shape so `FinancialBreakdown` and `RefundHistoryBlock` render it
 * exactly like every other REF surface. `null` when the order has no refund.
 *
 * Fields the endpoint does not return (channelGroup, fulfillment, salesSource, orderDate) are
 * filled with harmless defaults — neither component reads them, only `components`, `tenders`,
 * `type`, `reason`, `staffName` and the two timestamp fields are ever rendered here.
 */
function refundRecordFromDetail(detail: OrderDetailResponse | null, orderNo: any, orderType: string | null | undefined): RefundRecord | null {
    const rh = detail?.refundHistory;
    if (!rh) return null;

    // New BE shape sends components/tenders at the top level; legacy nests them in refundHistory.
    const rawComponents = rh.components ?? detail?.components ?? [];
    const rawTenders = rh.tenders ?? detail?.tenders ?? [];

    // One entry per refund action — never deduped (two 'Fee' rows can share a reason).
    const stampOf = (s?: string) => (s ? s.replace(' ', 'T') : '');
    const details: RefundDetail[] = (rh.refundDetails ?? []).map((e) => ({
        refundedAt: e.refundedAt ?? '',
        createdAt: stampOf(e.refundedAt),
        staffName: String(e.staffName ?? '').trim() || 'Unassigned',
        type: REFUND_TYPE_MAP[String(e.refundType ?? '').trim().toLowerCase()] ?? 'fee',
        reason: String(e.reason ?? '').trim() || NO_REASON,
    }));

    const components = emptyComponents();
    for (const c of rawComponents) {
        const key = String(c?.component ?? '').toLowerCase().replace(/[^a-z]/g, '');
        const mapped = REFUND_COMPONENT_MAP[key];
        if (mapped) components[mapped] = Number((components[mapped] + (Number(c.amount) || 0)).toFixed(2));
    }

    // Legacy flat fields win; with the new shape they're absent, so summarise from the entries
    // (Full > Item > Fee) and take the latest-listed entry's staff/time/reason as the headline.
    const lead = details[details.length - 1];
    const typeKey = String(rh.refundType ?? '').trim().toLowerCase();
    const detailType: RefundType | undefined = details.length
        ? (details.some((x) => x.type === 'full') ? 'full' : details.some((x) => x.type === 'item') ? 'item' : 'fee')
        : undefined;
    const type = REFUND_TYPE_MAP[typeKey] ?? detailType ?? deriveRefundType(components);
    const staffName = String(rh.staffName ?? '').trim() || lead?.staffName || 'Unassigned';
    const refundedAt = rh.refundedAt ?? lead?.refundedAt ?? '';
    const reasonText = rh.reason ?? lead?.reason;
    // Store local wall-clock time ('YYYY-MM-DD HH:mm:ss') reshaped to look ISO-ish so
    // RefundHistoryBlock's own formatter (which reads `createdAt`) can parse it — never
    // routed through `new Date()`, which would re-zone it to the viewer's own timezone.
    const isoStamp = stampOf(refundedAt);
    const tenders: RefundTenderReturn[] = rawTenders.map((t) => ({
        tender: [t.cardType, t.last4digit].filter(Boolean).join(' ').trim() || t.tenderType || 'Original tender',
        amount: Number(t.amount) || 0,
    }));

    return {
        id: `order-detail-refund-${orderNo}`,
        orderId: String(detail?.orderId ?? orderNo ?? ''),
        orderNo: String(orderNo ?? ''),
        orderDate: detail?.orderTime ?? '',
        refundedAt,
        type,
        components,
        // The API sends an empty string until a reason is captured on the refund.
        reason: String(reasonText ?? '').trim() || NO_REASON,
        reasonGroup: reasonGroupOf(reasonText),
        details,
        staffName,
        // REF-6 — the endpoint doesn't distinguish who OWNED the check from who processed
        // the refund; same default every other real-data REF surface uses.
        checkOwner: staffName,
        tenders,
        channel: orderType ?? '',
        channelGroup: 'IN_STORE',
        fulfillment: orderType ?? '',
        customerName: detail?.customerName ?? null,
        salesSource: 'direct',
        createdAt: isoStamp,
        updatedAt: isoStamp,
    };
}

function SummaryCard({ d, financials, refund, showServiceCharge = false }: { d: NormalisedDetail; financials?: OrderLogFinancials | null; refund?: RefundRecord | null; showServiceCharge?: boolean }) {
    // Subtotal, Discounts (+ offers) and Loyalty render explicitly below; these
    // are the rows that follow them, before the Total.
    const trailingRows = [
        { label: 'Tax', val: fmtCurrency(d.tax), color: T.t1 },
        { label: 'Tip', val: fmtCurrency(d.tip), color: T.green },
        // Reconciles Subtotal + Tax + Tip + Service Charge = Total; shown only for outlets that
        // capture a service charge, and only when non-zero, matching every other conditional row.
        ...(showServiceCharge && d.serviceCharge > 0 ? [{ label: 'Service Charge', val: fmtCurrency(d.serviceCharge), color: T.t1 }] : []),
        ...(d.gratuityAmt > 0 ? [{ label: 'Gratuity', val: fmtCurrency(d.gratuityAmt), color: T.green }] : []),
        ...(d.deliveryCharges > 0 ? [{ label: 'Delivery Fee', val: fmtCurrency(d.deliveryCharges), color: T.t1 }] : []),
        ...(d.convenienceFee > 0 ? [{ label: 'Convenience Fee', val: fmtCurrency(d.convenienceFee), color: T.t1 }] : []),
        ...(d.cardProcessingFee > 0 ? [{ label: 'Card Processing Fee', val: fmtCurrency(d.cardProcessingFee), color: T.t1 }] : []),
        ...(d.refundAmount > 0 ? [{ label: 'Refund Amount', val: fmtCurrency(d.refundAmount), color: T.red }] : []),
    ];

    return (
        <div className="ods-card">
            <div className="ods-card__label">Summary</div>

            <div className="ods-summary-row" style={{ borderBottom: `1px solid ${T.divider}` }}>
                <span className="ods-summary-label">Subtotal</span>
                <span className="ods-summary-val" style={{ color: T.t1 }}>{fmtCurrency(d.subtotal)}</span>
            </div>

            {(d.discount > 0 || d.offers.length > 0) && (
                <div style={{ borderBottom: `1px solid ${T.divider}` }}>
                    <div className="ods-summary-row" style={{ borderBottom: 'none' }}>
                        <span className="ods-summary-label">Discounts</span>
                        {d.discount > 0 && (
                            <span className="ods-summary-val" style={{ color: T.orange }}>–{fmtCurrency(d.discount)}</span>
                        )}
                    </div>
                    {d.offers.length > 0 && (
                        <div style={{ padding: '0 0 8px 12px' }}>
                            {d.offers.map((o, idx) => (
                                <div key={idx} className="ods-summary-row" style={{ borderBottom: 'none', padding: '2px 0' }}>
                                    <span className="ods-summary-label" style={{ color: T.t3, fontSize: 12 }}>{o.offerName}</span>
                                    <span className="ods-summary-val" style={{ color: T.orange, fontSize: 12 }}>–{fmtCurrency(o.offerDiscount)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {d.loyalty && (
                <div className="ods-summary-row" style={{ borderBottom: `1px solid ${T.divider}` }}>
                    <span className="ods-summary-label">{d.loyalty.loyaltypoints || 'Loyalty'}</span>
                    <span className="ods-summary-val" style={{ color: T.orange }}>–{fmtCurrency(d.loyalty.loyaltyValue)}</span>
                </div>
            )}

            {trailingRows.map(row => (
                <div key={row.label} className="ods-summary-row" style={{ borderBottom: `1px solid ${T.divider}` }}>
                    <span className="ods-summary-label">{row.label}</span>
                    <span className="ods-summary-val" style={{ color: row.color }}>{row.val}</span>
                </div>
            ))}
            <div className="ods-summary-total">
                <span className="ods-total-label">Total</span>
                <span className="ods-total-val" style={{ color: T.accent }}>{fmtCurrency(d.total)}</span>
            </div>
            {financials && <FinancialBreakdown f={financials} refund={refund} showServiceCharge={showServiceCharge} />}
        </div>
    );
}

// ─── main component ──────────────────────────────────────────────────────────

export interface OrderDetailSliderProps {
    visible: boolean;
    onClose: () => void;
    /** Shown in the panel header */
    orderNo?: string | number | null;
    /** Shown as subtitle in the panel header */
    orderType?: string | null;
    loading: boolean;
    error: boolean;
    onRetry: () => void;
    /** Typed Redux response (Today / ClickHouse path) */
    detail: OrderDetailResponse | null;
    /** Raw API response used as fallback for non-Today path in SalesScreen */
    legacyDetail?: any;
    /**
     * Financial waterfall for this order (ORD-1.4). Optional — the slider derives
     * it from `detail` itself; this only fills in when the detail response has
     * not arrived or does not carry the components.
     */
    financials?: OrderLogFinancials | null;
    /**
     * Every table the check touched (ORD-1.2). Merged checks show the full list
     * here even though the log row collapses it to "R4 +1".
     */
    tables?: string[];
}

export function OrderDetailSlider({
    visible,
    onClose,
    orderNo,
    orderType,
    loading,
    error,
    onRetry,
    detail,
    legacyDetail,
    financials = null,
    tables,
}: OrderDetailSliderProps) {
    const { scrollRef, pullDistance, isRefreshing } = usePullToRefresh({
        onRefresh: onRetry,
        disabled: !visible,
    });
    // Outlet captures a service charge? Gates its Summary and Financial Breakdown lines.
    const scEnabled = useServiceChargeEnabled();

    if (!visible) return null;

    const d = normalise(detail, legacyDetail);

    // REF-5 — real refund event off `detail.refundHistory` (the legacy payload carries
    // no refund data, so this is always null on that path).
    const refund: RefundRecord | null = refundRecordFromDetail(detail, orderNo, orderType);
    // The API's own status string ('Refunded' / 'Partially Refunded') is preferred over
    // the derived label — falls back to it only when the order has a refund event but the
    // endpoint didn't send the status string.
    const statusLabel = detail?.refundedOrderStatus || (refund ? refundStatusLabel(refund.type) : d?.orderStatus ?? '');

    return (
        <div className="ods-root" onClick={onClose}>
            <div className="ods-panel" onClick={e => e.stopPropagation()} style={{ background: T.surface }}>
                <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />

                {/* Header */}
                <div className="ods-header" style={{ borderBottom: `1px solid ${T.divider}` }}>
                    <div>
                        <div className="ods-header__title" style={{ color: T.t1 }}>
                            Order #{orderNo ?? '—'}
                        </div>
                        <div className="ods-header__subtitle" style={{ color: T.t3 }}>
                            {/* `orderType` prop covers callers whose row already carries it
                                (e.g. the Order Log table); callers that don't — like the
                                Discount Log, whose rows have no channel field at all — fall
                                back to the order-detail fetch's own `orderType` once it loads. */}
                            {orderType ?? d?.orderType ?? '—'}
                        </div>
                    </div>
                    <button
                        className="ods-close-btn"
                        onClick={onClose}
                        style={{ border: `1px solid ${T.cardBorder}`, background: T.hover, color: T.t2 }}
                    >
                        ✕
                    </button>
                </div>

                {/* Body */}
                <div className="ods-body" ref={scrollRef}>
                    {loading ? (
                        <div className="ods-loading">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Bone key={i} w={null} h={14} style={{ marginBottom: 12 }} />
                            ))}
                        </div>
                    ) : error ? (
                        <div className="ods-error">
                            <span className="material-symbols-rounded ods-error__icon" style={{ color: T.t3 }}>
                                error_outline
                            </span>
                            <div className="ods-error__msg" style={{ color: T.t3 }}>
                                Failed to load order details.
                            </div>
                            <button
                                className="ods-error__retry"
                                onClick={onRetry}
                                style={{ color: T.accent }}
                            >
                                Retry
                            </button>
                        </div>
                    ) : !d ? (
                        <div className="ods-empty" style={{ color: T.t3 }}>
                            No order details available.
                        </div>
                    ) : (
                        <>
                            {/* Time + Status */}
                            <div className="ods-meta">
                                <div className="ods-meta__block">
                                    <div className="ods-field-label" style={{ color: T.t3 }}>Order Time</div>
                                    <div className="ods-field-value" style={{ color: T.t1 }}>{d.orderTime}</div>
                                </div>
                                <div>
                                    <div className="ods-field-label" style={{ color: T.t3 }}>Status</div>
                                    {/* REF-5 — a refunded order says so in its status badge: 'Refunded' for a full
                                        refund, 'Partially Refunded' for an item or fee refund — reading the
                                        API's own `refundedOrderStatus` when it sends one. */}
                                    <span
                                        className="ods-status-badge"
                                        style={{ color: statusColor(statusLabel), background: `${statusColor(statusLabel)}15` }}
                                    >
                                        {statusLabel}
                                    </span>
                                </div>
                            </div>

                            {/* Customer */}
                            <div className="ods-card">
                                <div className="ods-card__label" style={{ color: T.t3 }}>Customer</div>
                                <div className="ods-customer-grid">
                                    <div>
                                        <div className="ods-field-label" style={{ color: T.t3 }}>Name</div>
                                        <div className="ods-customer-name" style={{ color: T.t1 }}>{d.customerName || 'Walk-in'}</div>
                                    </div>
                                    <div>
                                        <div className="ods-field-label" style={{ color: T.t3 }}>Phone</div>
                                        <div className="ods-field-value" style={{ color: T.t1 }}>{d.phone}</div>
                                    </div>
                                    {d.email && (
                                        <div>
                                            <div className="ods-field-label" style={{ color: T.t3 }}>Email</div>
                                            <div className="ods-field-value" style={{ color: T.t1 }}>{d.email}</div>
                                        </div>
                                    )}
                                    {d.guests != null && (
                                        <div>
                                            <div className="ods-field-label" style={{ color: T.t3 }}>Guests</div>
                                            <div className="ods-field-value" style={{ color: T.t1 }}>{d.guests}</div>
                                        </div>
                                    )}
                                    {(d.tableName !== undefined || (tables && tables.length > 0)) && (
                                        <div>
                                            <div className="ods-field-label" style={{ color: T.t3 }}>Table No.</div>
                                            <div className="ods-field-value" style={{ color: T.t1 }}>
                                                {tables && tables.length > 0 ? tables.join(', ') : (d.tableName || '—')}
                                            </div>
                                        </div>
                                    )}
                                    {d.isOffPremise && d.deliveryAddress && (
                                        <div className="ods-customer-address">
                                            <div className="ods-field-label" style={{ color: T.t3 }}>Delivery Address</div>
                                            <div className="ods-field-value" style={{ color: T.t1 }}>{d.deliveryAddress}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Items */}
                            {d.items.length > 0 && <ItemsTable items={d.items} />}

                            {/* Cancelled Items */}
                            {d.removedItems && d.removedItems.length > 0 && (
                                <CancelledItemsTable items={d.removedItems} />
                            )}

                            {/* Summary + financial waterfall */}
                            <SummaryCard d={d} financials={financialsFromDetail(detail, d, financials) ?? financials} refund={refund} showServiceCharge={scEnabled} />

                            {/* Staff */}
                            {d.staffName && (
                                <div className="ods-card">
                                    <div className="ods-card__label" style={{ color: T.t3 }}>Staff</div>
                                    <div className="ods-summary-row">
                                        <span className="ods-summary-label">Server</span>
                                        <span className="ods-staff-name" style={{ color: T.accent }}>{d.staffName}</span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

            </div>
        </div>
    );
}
